import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { SiteHeader } from "@/components/SiteHeader";
import { usePageMeta } from "@/lib/use-page-meta";
import { SOCIAL_LINKS } from "@/lib/socials";
import { ArrowRight, Linkedin, Instagram } from "lucide-react";

export default function About() {
  usePageMeta({
    title: "About",
    description:
      "Plainly is built by Nicholas Steer, a 20-year sales veteran and founder of Limitless Legacy Consulting. He created Plainly because clarity drives action — in sales, and in analytics.",
    path: "/about",
  });

  // Person + ProfilePage JSON-LD — Google's 2026 E-E-A-T ranking heavily
  // favours content that has a real, verifiable author with cross-site
  // identity signals. This lets Google connect the Plainly site to
  // Nicholas's LinkedIn and Instagram identities for author authority.
  useEffect(() => {
    const ld = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "ProfilePage",
          "@id": "https://myplainly.com/about#profilepage",
          url: "https://myplainly.com/about",
          mainEntity: { "@id": "https://myplainly.com/about#person" },
          breadcrumb: { "@id": "https://myplainly.com/about#breadcrumb" },
        },
        {
          "@type": "Person",
          "@id": "https://myplainly.com/about#person",
          name: "Nicholas Steer",
          jobTitle: "Founder",
          worksFor: { "@id": "https://myplainly.com/#organization" },
          url: "https://myplainly.com/about",
          description:
            "Founder of Plainly and Limitless Legacy Consulting LLC. 20+ years in sales — in-home, B2B, and RTO retail — building backend sales infrastructures and customer-facing presentations.",
          sameAs: [SOCIAL_LINKS.linkedin.url, SOCIAL_LINKS.instagram.url],
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://myplainly.com/about#breadcrumb",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://myplainly.com/" },
            { "@type": "ListItem", position: 2, name: "About", item: "https://myplainly.com/about" },
          ],
        },
      ],
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "about-person-jsonld";
    script.textContent = JSON.stringify(ld);
    document.head.appendChild(script);
    return () => { script.remove(); };
  }, []);

  return (
    <div className="min-h-screen bg-[#F7F5F0] text-[#1E392A]">
      <SiteHeader />
      <main className="max-w-3xl mx-auto px-6 md:px-8 py-12 md:py-20">
        <div className="tag-label text-[#E27D60] mb-3">About</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
          Built for the people who close the sale, not the people who built the chart.
        </h1>

        <div className="mt-10 flex items-center gap-4">
          <div
            className="w-16 h-16 rounded-full grid place-items-center text-xl font-display text-white shrink-0"
            style={{ backgroundColor: "#1E392A" }}
            aria-hidden="true"
          >
            NS
          </div>
          <div>
            <div className="font-display text-2xl">Nicholas Steer</div>
            <div className="text-sm text-[#1E392A]/65">Founder, Plainly · Limitless Legacy Consulting LLC</div>
            <div className="mt-2 flex items-center gap-3">
              <a
                href={SOCIAL_LINKS.linkedin.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Nicholas Steer on LinkedIn"
                className="inline-flex items-center gap-1.5 text-xs text-[#1E392A]/65 hover:text-[#E27D60] transition-colors"
                data-testid="about-linkedin-link"
              >
                <Linkedin className="w-3.5 h-3.5" /> LinkedIn
              </a>
              <a
                href={SOCIAL_LINKS.instagram.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Plainly on Instagram"
                className="inline-flex items-center gap-1.5 text-xs text-[#1E392A]/65 hover:text-[#E27D60] transition-colors"
                data-testid="about-instagram-link"
              >
                <Instagram className="w-3.5 h-3.5" /> Instagram
              </a>
            </div>
          </div>
        </div>

        <div className="mt-10 space-y-5 text-[#1E392A]/85 leading-relaxed text-lg">
          <p>
            I&apos;ve spent more than 20 years in the sales world — from in-home and B2B to RTO retail —
            building systems, leading teams, and creating customer-facing presentations that
            actually move people to take action. Over the years, I&apos;ve designed backend sales
            infrastructures, developed websites for multiple companies, and learned firsthand how
            clarity drives performance.
          </p>
          <p>
            I co-founded a home improvement sales company and later launched{" "}
            <span className="font-medium">Limitless Legacy Consulting LLC</span>, where I help
            businesses streamline their sales processes and scale with confidence.
          </p>
          <p>
            I created <span className="font-medium">Plainly</span> because I believe the same rule
            that drives great sales applies to data: break it down in plain English so anyone can
            understand it. After watching teams avoid Google Analytics like the plague, I knew
            there had to be a better way. Plainly turns confusing data into simple, human-readable
            insights — no jargon, no guesswork, just clarity.
          </p>
        </div>

        <div className="mt-14 rounded-3xl bg-[#1E392A] text-[#F7F5F0] p-7 md:p-9">
          <div className="tag-label text-[#E27D60]">Why Plainly exists</div>
          <h2 className="font-display text-2xl md:text-3xl mt-2 leading-tight">
            If you can&apos;t explain a number simply, the number doesn&apos;t belong on the page.
          </h2>
          <p className="mt-4 text-[#F7F5F0]/80 leading-relaxed">
            That&apos;s the rule we run Plainly by. Every report you read, every Monday-morning email
            you receive, has been filtered through one question: would a busy business owner
            understand this in 30 seconds?
          </p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#E27D60] hover:bg-[#d76d50] text-white font-medium px-5 h-10"
            data-testid="about-cta-try-plainly"
          >
            Try the free demo <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <p className="text-sm text-[#1E392A]/55 mt-12">
          Questions, feedback, or partnership ideas?{" "}
          <a href="mailto:office@myplainly.com" className="underline text-[#1E392A]">
            office@myplainly.com
          </a>
          .
        </p>
      </main>
    </div>
  );
}
